import merge from 'lodash.merge';
import { renderHome } from './components/home';
import { renderSettings } from './components/settings';
import { classNamePrefix, localStorageName } from './globals';
import { TChoices, TConfig, TDefaultConfig, TStorage, TUserConfig } from './types';

const defaultConfig: TDefaultConfig = {
    translations: {
        en: {
            privacyPolicy: {
                label: 'Privacy policy'
            },
            home: {
                title: 'Cookie management',
                description: 'By allowing third-party services, you accept the creation and use of cookies as well as the tracking technologies necessary for their usage.',
                decline: 'Decline',
                configure: 'Configure',
                accept: 'Accept'
            },
            settings: {
                title: 'Our cookies',
                description: 'Here you can select which services are allowed to use cookies.',
                checkAll: 'Check all',
                uncheckAll: 'Uncheck all',
                back: 'Back',
                done: 'Done'
            }
        },
        fr: {
            privacyPolicy: {
                label: 'Politique de confidentialité'
            },
            home: {
                title: 'Gestion des cookies',
                description: 'En autorisant des services tiers, vous acceptez la création et l\'utilisation de cookies ainsi que des technologies de suivi nécessaires à leur utilisation.',
                decline: 'Refuser',
                configure: 'Configurer',
                accept: 'Accepter'
            },
            settings: {
                title: 'Nos cookies',
                description: 'Ici vous pouvez sélectionner quels services sont autorisés à utiliser des cookies.',
                checkAll: 'Tout cocher',
                uncheckAll: 'Tout décocher',
                back: 'Retour',
                done: 'Terminer'
            }
        }
    }
};

const defaultStorage: TStorage = {
    uuid: self.crypto.randomUUID(),
    choices: {},
    timestamp: new Date().getTime()
};

export default class SimpleConsentManager {
    private config: TConfig;
    private storage: TStorage;
    private lastRecordedChoices: TChoices = {};

    private containerElement: HTMLDivElement;
    private homeElement: HTMLDivElement;
    private settingsElement: HTMLDivElement;

    private containerOpenClass = `${classNamePrefix}-container--open`;

    constructor (userConfig: TUserConfig) {
        // Create final config
        this.config = merge(defaultConfig, userConfig);

        // Get string from local storage
        const storageString = localStorage.getItem(localStorageName);

        if (storageString) {
            // Parse storage string
            this.storage = JSON.parse(storageString) as TStorage;
        } else {
            // Create default storage
            this.storage = structuredClone(defaultStorage);

            // Send default records
            this.sendRecords();
        }

        // Save current choices for comparison
        this.lastRecordedChoices = structuredClone(this.storage.choices);

        // Check that each service has a matching choice
        Object.entries(this.config.services).forEach(([serviceName, service]) => {
            // If the service doesn't have a matching choice
            if (this.storage.choices[serviceName] === undefined) {
                // If the service is required
                if (service.required) {
                    // The choice is set to "true"
                    this.setChoice(serviceName, true);
                } else {
                    // The choice is set to "null" to query the user
                    this.setChoice(serviceName, null);
                }
            } else {
                // Execute service callback
                if (service.callback) {
                    service.callback(this.storage.choices[serviceName]);
                }
            }
        });

        // Check that each choice has a matching service
        Object.entries(this.storage.choices).forEach(([serviceName]) => {
            // If the choice doesn't have a mathching service
            if (!this.config.services[serviceName]) {
                // Delete choice
                this.deleteChoice(serviceName);
            }
        });

        // Send new records
        this.sendRecords();

        // Render container
        this.containerElement = document.createElement('div');
        this.containerElement.classList.add(classNamePrefix + '-container');
        document.body.prepend(this.containerElement);

        // Render home
        this.homeElement = renderHome(
            this.config,
            () => this.declineAllChoices(),
            () => this.acceptAllChoices(),
            () => this.showSettings()
        );
        this.containerElement.appendChild(this.homeElement);

        // Render settings
        this.settingsElement = renderSettings(
            this.config,
            () => this.getChoices(),
            () => this.sendRecords(),
            (serviceName, value) => this.setChoice(serviceName, value),
            () => this.showHome(true),
            () => this.close()
        );
        this.containerElement.appendChild(this.settingsElement);

        // Check if a choice is required
        let choiceRequired = false

        Object.values(this.storage.choices).forEach((value) => {
            if (value === null) {
                choiceRequired = true;
            }
        });

        if (choiceRequired) {
            this.open();
        }
    }

    private getChoices () {
        return this.storage.choices;
    }

    private setChoice (serviceName: string, value: boolean | null) {
        // Check if the new value is different from the current one
        if (this.storage.choices[serviceName] === value) {
            return;
        }

        // Update class property
        this.storage.choices[serviceName] = value;
        this.storage.timestamp = new Date().getTime();

        // Update local storage
        localStorage.setItem(localStorageName, JSON.stringify(this.storage));

        // Update checkbox
        if (this.settingsElement) {
            const checkboxElement = this.settingsElement.querySelector(`input[name=${serviceName}]`) as HTMLInputElement;

            checkboxElement.checked = !!value;
        }

        // Notify callback with new choice
        if (this.config.services && this.config.services[serviceName] && this.config.services[serviceName].callback) {
            this.config.services[serviceName].callback(this.storage.choices[serviceName]);
        }
    }

    private sendRecords () {
        const { recordsUrl } = this.config;

        if (recordsUrl) {
            // Check if the current choices are identical to the last recorded ones
            if (JSON.stringify(this.lastRecordedChoices) === JSON.stringify(this.storage.choices)) {
                return;
            }

            // Send records to endpoint
            fetch(recordsUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(this.storage)
            });

            // Update last recorded choices
            this.lastRecordedChoices = structuredClone(this.storage.choices);
        }
    }

    private deleteChoice (serviceName: string) {
        // Update class property
        delete this.storage.choices[serviceName];

        // Update local storage
        localStorage.setItem(localStorageName, JSON.stringify(this.storage));
    }

    private acceptAllChoices () {
        // Set the choise to "true" for each service
        Object.keys(this.config.services).forEach((serviceName) => {
            if (this.storage.choices[serviceName] !== true) {
                this.setChoice(serviceName, true);
            }
        });

        // Send new records
        this.sendRecords();

        this.close();
    }

    private declineAllChoices () {
        // Set the choise to "false" for each service that isn't required
        Object.entries(this.config.services).forEach(([serviceName, service]) => {
            if (!service.required && this.storage.choices[serviceName] !== false) {
                this.setChoice(serviceName, false);
            }
        });

        // Send new records
        this.sendRecords();

        this.close();
    }

    private showHome (back?: boolean) {
        this.homeElement.style.display = 'flex';
        this.settingsElement.style.display = 'none';

        if (back) {
            // Focus configure button
            const buttonElement = this.homeElement.querySelector(`.${classNamePrefix}-home__button-configure`) as HTMLButtonElement;
            buttonElement.focus();
        } else {
            // Focus link
            const linkElement = this.homeElement.querySelector(`.${classNamePrefix}-home__link`) as HTMLAnchorElement;
            linkElement.focus();
        }
    }

    private showSettings (serviceName?: string) {
        this.homeElement.style.display = 'none';
        this.settingsElement.style.display = 'flex';

        if (serviceName) {
            if (this.config.services[serviceName]) {
                // Focus checkbox matching service
                const checkboxElement = this.settingsElement.querySelector(`input[name=${serviceName}]`) as HTMLInputElement;
                checkboxElement.focus();
            } else {
                console.error(`Could not find service "${serviceName}"`);

                // Focus uncheck all button
                const buttonElement = this.settingsElement.querySelector(`.${classNamePrefix}-settings__button-uncheck-all`) as HTMLButtonElement;
                buttonElement.focus();
            }
        } else {
            // Focus uncheck all button
            const buttonElement = this.settingsElement.querySelector(`.${classNamePrefix}-settings__button-uncheck-all`) as HTMLButtonElement;
            buttonElement.focus();
        }

    }

    public open (serviceName?: string) {
        this.containerElement.classList.add(this.containerOpenClass);
        this.containerElement.inert = false;

        if (serviceName) {
            this.showSettings(serviceName);
        } else {
            this.showHome();
        }
    }

    public close () {
        this.containerElement.inert = true;
        this.containerElement.classList.remove(this.containerOpenClass);
    }
}